import React,{ useCallback, useState } from 'react'
import { useActivateCustomerMutation, useDeactivateCustomerMutation, useGetAllCustomersQuery, useGetCustomersQuery } from '../../backoffice/apis/customerAPI'
import { Container, ListGroup, ListGroupItem, Row, Col, Button, Input, Spinner } from 'reactstrap'
import CustomerSearch from './CustomerSearch'
import { Customer } from '../../backoffice/dtos/customer/customer'
import CustomerEditModal from './CustomerEditModal'
import "../../App.css"
import { confirmAlert } from 'react-confirm-alert'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { getMessageFromRtkError } from '../../backoffice/apis/baseAPI'

export function CustomersSettings(
    {
        shown,
        isAdmin
    }: {
        shown: boolean,
        isAdmin: boolean
    }
) {
    if (isAdmin) {
        return <AdminUserCustomers shown={shown} isAdmin={isAdmin} />
    } else {
        return <SimpleUserCustomers shown={shown} isAdmin={isAdmin} />
    }
}

function SimpleUserCustomers(
    {
        shown,
        isAdmin
    }: {
        shown: boolean,
        isAdmin: boolean
    }
) {
    const [searchTerm, setSearchTerm] = useState<string>("")
    const {data: customers} = useGetCustomersQuery({query: searchTerm, includeDeleted: isAdmin}, {skip: !shown, refetchOnMountOrArgChange: true})

    return <Customers shown={shown} isAdmin={isAdmin} customers={customers} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
}

function AdminUserCustomers(
    {
        shown,
        isAdmin
    }: {
        shown: boolean,
        isAdmin: boolean
    }
) {
    const [searchTerm, setSearchTerm] = useState<string>("")
    const {data: customers} = useGetAllCustomersQuery({query: searchTerm, includeDeleted: isAdmin}, {skip: !shown, refetchOnMountOrArgChange: true})

    return <Customers  shown={shown} isAdmin={isAdmin} customers={customers} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
}

function Customers(
    {
        shown,
        isAdmin,
        customers,
        searchTerm,
        setSearchTerm
    }: {
        shown: boolean,
        isAdmin: boolean,
        customers: Customer[] | undefined,
        searchTerm: string,
        setSearchTerm: React.Dispatch<React.SetStateAction<string>>
    }
) {

    const [loading, setLoading] = useState(false)
    const {t} = useTranslation(['customers', 'common'])
    const [customerModal, setCustomerModal] = useState<{ isOpen: boolean; customer ?: Customer }>()

    const [ deactivateCustomer ] = useDeactivateCustomerMutation()
    const [ activateCustomer ] = useActivateCustomerMutation()


    const openCustomerModal = useCallback((customer: Customer | undefined) => {
        setCustomerModal({ isOpen: true, customer })
    }, []);

    const closeCustomerModal = useCallback(() => {
        setCustomerModal({ isOpen: false })
    }, []);

    const deactivateCustomerAsync = useCallback(async (id: any) => {
        try {
            setLoading(true);
            await deactivateCustomer(id).unwrap()
            toast( t('successful_delete_alert') )
        } catch (e:any) {
            toast(getMessageFromRtkError(e))
        } finally {
            setLoading(false);
        }
    }, []);

    const activateCustomerAsync = useCallback(async (id: any) =>{
        try {
            setLoading(true);
            await activateCustomer(id).unwrap()
            toast( t('Customer enabled') )
        } catch (e:any) {
            toast(e.data.message)
        } finally {
            setLoading(false)
        }
    }, []);

    const handleDeleteCustomer = useCallback((id: number, customer: string) =>{
        confirmAlert({
            title: t('delete_alert_title'),
            message: t('delete_alert_description',{customer: customer}),
            buttons: [{ label: t('yes',{ns:'common'}), onClick:() => deactivateCustomerAsync(id) }, { label: t('no',{ns:'common'}) }]
        })
    }, []);

    const handleActivateDeleteCustomer = useCallback((id: number, customer: string) =>{
        confirmAlert({
            title: t('activate_alert_title'),
            message: t('activate_alert_description',{customer: customer}),
            buttons: [{ label: t('yes',{ns:'common'}),onClick:() => activateCustomerAsync(id) }, { label: t('no',{ns:'common'})}]
        })
    }, []);

    return (
        <Container>
            <CustomerSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} isLoading={loading} openCustomerModal={openCustomerModal}/>
            <ListGroup className='mt-4'>
                <ListGroupItem>
                    <Row>
                        <Col lg={4}><h5>{t('Name', { ns: 'common' })}</h5></Col>
                        <Col lg={3}><h5>{t('Short Name', { ns: 'common' })}</h5></Col>
                        <Col lg={2}><h5>Payer Code </h5></Col>
                        <Col lg={2}><h5>Vendor Code </h5></Col>
                    </Row>
                </ListGroupItem>
                {customers && customers.map((customer, index) =>
                    <ListGroupItem key={index}>
                        <Row divider>
                            <Col lg={4} className='bold-text d-flex flex-column justify-content-center'>{customer.description}</Col>
                            <Col lg={3} className='bold-text d-flex flex-column justify-content-center'>{customer.shortDescription}</Col>
                            <Col lg={2} className='d-flex flex-column justify-content-center'>{customer.payerOfAccountCode}</Col>
                            <Col lg={2} className='d-flex flex-column justify-content-center'>{customer.vendorCode}</Col>
                            <Col lg={1} className="d-flex justify-content-end" >
                                <Row>
                                    <Button color="primary" className='p-1' onClick={() => openCustomerModal(customer)} disabled={loading}>
                                        <i className='fa fa-edit fa-lg'></i>{loading && <Spinner size='sm' className='ms-2' />}
                                    </Button>
                                    {
                                        isAdmin && (
                                            <>
                                                {
                                                    !customer.isDeleted && (
                                                        <Button color="danger" className="mt-2 p-1" onClick={() => handleDeleteCustomer(customer.id, customer.shortDescription)} disabled={loading}>
                                                            <i className='fa fa-trash fa-lg'></i>{loading && <Spinner size='sm' className='ms-2' />}
                                                        </Button>
                                                    )
                                                }
                                                {
                                                    customer.isDeleted && (
                                                        <Button color="success" className='mt-2 p-1' onClick={() => handleActivateDeleteCustomer(customer.id, customer.shortDescription)} disabled={loading}>
                                                            <i className='fa fa-check fa-lg'></i>{loading && <Spinner size='sm' className='ms-2' />}
                                                        </Button>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                </Row>
                            </Col>
                        </Row>
                    </ListGroupItem>

                )}
            </ListGroup>
            {
            customerModal && (
                <CustomerEditModal isOpen={customerModal.isOpen} toggle={closeCustomerModal} selectedCustomer={customerModal.customer} />
            )
            }
        </Container>
    )
}
